/**
 * User management service
 * Calls the deployed IAS Auth/User-Management APIs.
 * Uses actual IAS role names and RbacIds — no hardcoded ADMN/MNGR/IOWN mapping.
 */
import { iasClient } from '../config/http.client';

// ── In-memory cache ───────────────────────────────────────────────────────
let _rolesCache = null;

/** Read decoded IAS token payload from cache. */
const getDecodedTokenData = () => {
  try {
    return JSON.parse(localStorage.getItem('decodedTokenData') || '{}');
  } catch {
    return {};
  }
};

/** Read AccountId from cached decoded IAS token. */
const getAccountId = () => {
  const d = getDecodedTokenData();
  return d.AccountId || '';
};

/** Read ApplicationId from cached decoded IAS token. */
const getApplicationId = () => {
  const d = getDecodedTokenData();
  return d.ApplicationId || '';
};

// ── RBAC roles ────────────────────────────────────────────────────────────

/**
 * Fetch all RBAC role definitions.
 * Returns array of { RbacId, RoleName, Description, Status, ... }
 * Cached per session.
 */
export const getRoles = async () => {
  if (_rolesCache) return _rolesCache;
  const res = await iasClient.get('/rbac/rbac', { params: { skip: 0, take: 500 } });
  const roles = res.data?.roles ?? (Array.isArray(res.data) ? res.data : []);
  _rolesCache = roles;
  return roles;
};

// ── Read ──────────────────────────────────────────────────────────────────

/** Fetch all users from IAS User-Management service. */
export const getUsers = async () => {
  const allRes = await iasClient.get('/user-management/user', { params: { skip: 0, take: 500 } });
  let inactiveRes = { data: [] };
  try {
    inactiveRes = await iasClient.get('/user-management/user', { params: { skip: 0, take: 500, status: 0 } });
  } catch {
    // Some IAS environments do not support status filter for this endpoint.
  }

  const all = allRes.data?.users ?? (Array.isArray(allRes.data) ? allRes.data : []);
  const inactive = inactiveRes.data?.users ?? (Array.isArray(inactiveRes.data) ? inactiveRes.data : []);

  const byId = new Map();
  [...all, ...inactive].forEach((u) => {
    if (u?.UserId && !byId.has(u.UserId)) byId.set(u.UserId, u);
  });
  return Array.from(byId.values());
};

/** Fetch all user-account associations. */
export const getUserAssociations = async () => {
  const allRes = await iasClient.get('/account/userassociation', { params: { skip: 0, take: 500 } });
  let inactiveRes = { data: [] };
  try {
    inactiveRes = await iasClient.get('/account/userassociation', { params: { skip: 0, take: 500, status: 0 } });
  } catch {
    // Some IAS environments do not support status filter for this endpoint.
  }

  const all = allRes.data?.userAssociation ?? (Array.isArray(allRes.data) ? allRes.data : []);
  const inactive = inactiveRes.data?.userAssociation ?? (Array.isArray(inactiveRes.data) ? inactiveRes.data : []);

  const byId = new Map();
  [...all, ...inactive].forEach((a, idx) => {
    const key = a?.UserAssociationId || `${a?.AccountId || ''}#${a?.UserId || ''}#${idx}`;
    if (!byId.has(key)) byId.set(key, a);
  });
  return Array.from(byId.values());
};

/**
 * Fetch users merged with their association info.
 * For users with multiple associations, picks the most recently modified one.
 * Returns [{...iasUser, roleName, rbacId, assocStatus, assocEtag, assocAppId, assocAccId}]
 */
export const getUsersWithRoles = async () => {
  const accountId = getAccountId();
  const applicationId = getApplicationId();
  const [users, associations] = await Promise.all([getUsers(), getUserAssociations()]);

  const usersById = new Map();
  users.forEach((u) => {
    if (u?.UserId) usersById.set(u.UserId, u);
  });

  // Choose the best association per user:
  // 1) same account + same application
  // 2) same account
  // 3) latest modified
  const assocMap = {};
  for (const a of associations) {
    if (!a.UserId) continue;

    const current = assocMap[a.UserId];
    if (!current) {
      assocMap[a.UserId] = a;
      continue;
    }

    const score = (assoc) => {
      let s = 0;
      if (accountId && assoc?.AccountId === accountId) s += 10;
      if (applicationId && assoc?.ApplicationId === applicationId) s += 5;
      return s;
    };

    const currentScore = score(current);
    const nextScore = score(a);
    if (nextScore > currentScore) {
      assocMap[a.UserId] = a;
      continue;
    }
    if (nextScore === currentScore) {
      const currTs = new Date(current.ModifiedDate || current.CreatedDate || 0).getTime();
      const nextTs = new Date(a.ModifiedDate || a.CreatedDate || 0).getTime();
      if (nextTs > currTs) assocMap[a.UserId] = a;
    }
  }

  // Include users present only in associations (e.g., inactive or partially provisioned users)
  Object.values(assocMap).forEach((a) => {
    if (!usersById.has(a.UserId)) {
      usersById.set(a.UserId, {
        UserId: a.UserId,
        Email: a.UserEmail || a.Email || '',
        Name: a.UserName || a.Name || a.AccountName || '',
        Status: a.Status,
      });
    }
  });

  return Array.from(usersById.values()).map((u) => {
    const assoc = assocMap[u.UserId];
    return {
      ...u,
      roleName: assoc?.RoleName ?? null,
      rbacId: assoc?.RbacId ?? null,
      assocStatus: assoc?.Status ?? u.Status,
      assocEtag: assoc?.etag ?? null,
      assocId: assoc?.UserAssociationId ?? null,
      assocAppId: assoc?.ApplicationId ?? null,
      assocAccId: assoc?.AccountId ?? null,
    };
  });
};

/**
 * Return all users as potential managers.
 * IES-specific roles (MNGR) may not exist in IAS yet, so return everyone.
 */
export const getManagers = async () => {
  const users = await getUsers();
  return {
    managers: users
      .filter((u) => (u?.Email || u?.email))
      .map((u) => ({
        email: String(u.Email || u.email || '').toLowerCase(),
        display_name: u.Name || u.name || u.Email || u.email,
      })),
  };
};

// ── Write ─────────────────────────────────────────────────────────────────

/**
 * Create a new user in IAS then associate them with the current account.
 * @param {{ name: string, email: string, description?: string, phone?: string, rbacId?: string }} fields
 */
export const createUser = async ({ name, email, description = '', phone = '', rbacId = '' }) => {
  const accountId = getAccountId();

  if (!rbacId) {
    throw new Error('Role is required');
  }

  const createRes = await iasClient.post('/user-management/user', {
    Name:        name,
    Email:       email,
    Description: description || name,
    PhoneNo:     phone,
  });
  const userId = createRes.data?.UserId;

  if (accountId && rbacId && userId) {
    await iasClient.post(`/account/account/${accountId}/User`, {
      UserId: userId,
      RbacId: rbacId,
    });
  }
  return createRes.data;
};

/**
 * Update a user's role or active status via their account association.
 * @param {string} userId
 * @param {{ rbacId?: string, active?: boolean }} fields
 */
export const updateUser = async (userId, { rbacId, active }) => {
  const accountId = getAccountId();

  if (!accountId) {
    throw new Error('AccountId is missing from token');
  }

  // Fetch current association for etag + current values
  const assocRes = await iasClient.get(`/account/userassociation/${userId}`);
  const assocs   = Array.isArray(assocRes.data)
    ? assocRes.data
    : (assocRes.data?.userAssociation ?? [assocRes.data]);

  // Prefer the association belonging to the current account
  const assoc = assocs.find(a => a.AccountId === accountId) ?? assocs[0];

  const resolvedRbacId = rbacId || assoc?.RbacId;
  if (!resolvedRbacId) {
    throw new Error('RbacId is required');
  }

  await iasClient.put(`/account/account/${accountId}/User/${userId}`, {
    UserAssociationId: assoc?.UserAssociationId || `${accountId}#${userId}`,
    UserId:        userId,
    AccountId:     accountId,
    ApplicationId: assoc?.ApplicationId,
    RbacId:        resolvedRbacId,
    Status:        active !== undefined ? (active ? 1 : 0) : assoc?.Status,
    etag:          assoc?.etag,
  });
};


